// Offline Storage and Queue Management
import { openDB, DBSchema, IDBPDatabase } from 'idb';
import { CSRF_HEADER_NAME, getCsrfToken } from './csrf';

interface OfflineRequest {
  id: string;
  method: string;
  url: string;
  data: any;
  timestamp: string;
  retryCount: number;
}

interface CachedResponse {
  url: string;
  data: any;
  timestamp: string;
  expiresAt: string;
}

interface VineBlocksDB extends DBSchema {
  offlineQueue: {
    key: string;
    value: OfflineRequest[];
    indexes: {
      timestamp: string;
    };
  };
  apiCache: {
    key: string;
    value: CachedResponse;
    indexes: {
      timestamp: string;
      expiresAt: string;
    };
  };
  formData: {
    key: string;
    value: {
      formId: string;
      formType: string;
      data: any;
      timestamp: string;
    };
    indexes: {
      formType: string;
      timestamp: string;
    };
  };
}

class OfflineManager {
  private db: IDBPDatabase<VineBlocksDB> | null = null;
  private isOnline: boolean = true;
  private syncInProgress: boolean = false;
  private queueListeners: Array<(queueLength: number) => void> = [];
  private connectivityListeners: Array<(isOnline: boolean) => void> = [];

  constructor() {
    this.init();
    this.setupConnectivityDetection();
    this.setupServiceWorkerCommunication();
  }

  private async init() {
    try {
      this.db = await openDB<VineBlocksDB>('VineBlocksOffline', 1, {
        upgrade(db) {
          // Offline queue store
          if (!db.objectStoreNames.contains('offlineQueue')) {
            const store = db.createObjectStore('offlineQueue', { keyPath: 'key' });
            store.createIndex('timestamp', 'timestamp', { unique: false });
          }

          // API cache store
          if (!db.objectStoreNames.contains('apiCache')) {
            const store = db.createObjectStore('apiCache', { keyPath: 'url' });
            store.createIndex('timestamp', 'timestamp', { unique: false });
            store.createIndex('expiresAt', 'expiresAt', { unique: false });
          }

          // Form data store for draft saving
          if (!db.objectStoreNames.contains('formData')) {
            const store = db.createObjectStore('formData', { keyPath: 'key' });
            store.createIndex('formType', 'formType', { unique: false });
            store.createIndex('timestamp', 'timestamp', { unique: false });
          }
        },
      });
      console.log('Offline database initialized');
    } catch (error) {
      console.error('Failed to initialize offline database:', error);
    }
  }

  private setupConnectivityDetection() {
    // Listen for online/offline events
    window.addEventListener('online', () => {
      console.log('Connection restored');
      this.isOnline = true;
      this.notifyConnectivityListeners(true);
      this.syncOfflineQueue();
    });

    window.addEventListener('offline', () => {
      console.log('Connection lost');
      this.isOnline = false;
      this.notifyConnectivityListeners(false);
    });

    // Initial connectivity check
    this.isOnline = navigator.onLine;
  }

  private setupServiceWorkerCommunication() {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', (event) => {
        const { type, data } = event.data;
        
        switch (type) {
          case 'OFFLINE_QUEUE_UPDATED':
            this.notifyQueueListeners(data.queueLength);
            break;
          case 'SYNC_COMPLETED':
            console.log('Sync completed, remaining requests:', data.remainingRequests);
            this.syncInProgress = false;
            break;
          case 'OFFLINE_QUEUE_CLEARED':
            this.notifyQueueListeners(0);
            break;
        }
      });
    }
  }

  // Public API methods
  public isConnected(): boolean {
    return this.isOnline;
  }

  public async queueRequest(method: string, url: string, data: any): Promise<string> {
    const request: OfflineRequest = {
      id: this.generateRequestId(),
      method,
      url,
      data,
      timestamp: new Date().toISOString(),
      retryCount: 0
    };

    if (this.isOnline) {
      // Try to send immediately
      try {
        const response = await fetch(url, {
          method,
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(data)
        });

        if (response.ok) {
          console.log('Request sent successfully:', request.id);
          return request.id;
        } else {
          throw new Error(`HTTP ${response.status}`);
        }
      } catch (error) {
        console.log('Network request failed, queuing offline:', error);
        this.isOnline = false;
        this.notifyConnectivityListeners(false);
      }
    }

    // Queue for offline processing
    await this.addToQueue(request);
    return request.id;
  }

  public async getCachedData(url: string): Promise<any> {
    if (!this.db) return null;

    try {
      const cached = await this.db.get('apiCache', url);
      
      if (cached && new Date(cached.expiresAt) > new Date()) {
        console.log('Returning cached data for:', url);
        return cached.data;
      } else if (cached) {
        // Remove expired cache
        await this.db.delete('apiCache', url);
      }
    } catch (error) {
      console.error('Error getting cached data:', error);
    }

    return null;
  }

  public async cacheData(url: string, data: any, ttlMinutes: number = 5): Promise<void> {
    if (!this.db) return;

    try {
      const expiresAt = new Date();
      expiresAt.setMinutes(expiresAt.getMinutes() + ttlMinutes);

      const cached: CachedResponse = {
        url,
        data,
        timestamp: new Date().toISOString(),
        expiresAt: expiresAt.toISOString()
      };

      await this.db.put('apiCache', cached);
    } catch (error) {
      console.error('Error caching data:', error);
    }
  }

  public async saveFormDraft(formId: string, formType: string, data: any): Promise<void> {
    if (!this.db) return;

    try {
      const formData = {
        key: formId,
        formId,
        formType,
        data,
        timestamp: new Date().toISOString()
      };

      await this.db.put('formData', formData);
      console.log('Form draft saved:', formId);
    } catch (error) {
      console.error('Error saving form draft:', error);
    }
  }

  public async getFormDraft(formId: string): Promise<any> {
    if (!this.db) return null;

    try {
      const draft = await this.db.get('formData', formId);
      return draft?.data || null;
    } catch (error) {
      console.error('Error getting form draft:', error);
      return null;
    }
  }

  public async clearFormDraft(formId: string): Promise<void> {
    if (!this.db) return;

    try {
      await this.db.delete('formData', formId);
      console.log('Form draft cleared:', formId);
    } catch (error) {
      console.error('Error clearing form draft:', error);
    }
  }

  public async getQueueStatus(): Promise<{ queueLength: number; requests: OfflineRequest[] }> {
    if (!this.db) return { queueLength: 0, requests: [] };

    try {
      const queue = await this.db.get('offlineQueue', 'queue');
      return {
        queueLength: queue?.length || 0,
        requests: queue || []
      };
    } catch (error) {
      console.error('Error getting queue status:', error);
      return { queueLength: 0, requests: [] };
    }
  }

  public async syncOfflineQueue(): Promise<void> {
    if (!this.isOnline || this.syncInProgress) return;

    this.syncInProgress = true;
    console.log('Starting offline queue sync...');

    try {
      const { requests } = await this.getQueueStatus();
      
      for (const request of requests) {
        try {
          const csrfToken = getCsrfToken();
          const response = await fetch(request.url, {
            method: request.method,
            credentials: 'include',
            headers: {
              'Content-Type': 'application/json',
              ...(csrfToken ? { [CSRF_HEADER_NAME]: csrfToken } : {}),
            },
            body: JSON.stringify(request.data)
          });

          if (response.ok) {
            console.log('Successfully synced request:', request.id);
            await this.removeFromQueue(request.id);
          } else {
            console.log('Failed to sync request:', request.id, response.status);
            await this.incrementRetryCount(request.id);
          }
        } catch (error) {
          console.error('Error syncing request:', request.id, error);
          await this.incrementRetryCount(request.id);
        }
      }

      console.log('Offline queue sync completed');
    } catch (error) {
      console.error('Error syncing offline queue:', error);
    } finally {
      this.syncInProgress = false;
    }
  }

  public async clearOfflineQueue(): Promise<void> {
    if (!this.db) return;

    try {
      await this.db.clear('offlineQueue');
      this.notifyQueueListeners(0);
      console.log('Offline queue cleared');
    } catch (error) {
      console.error('Error clearing offline queue:', error);
    }
  }

  // Event listeners
  public onQueueUpdate(callback: (queueLength: number) => void): () => void {
    this.queueListeners.push(callback);
    return () => {
      const index = this.queueListeners.indexOf(callback);
      if (index > -1) {
        this.queueListeners.splice(index, 1);
      }
    };
  }

  public onConnectivityChange(callback: (isOnline: boolean) => void): () => void {
    this.connectivityListeners.push(callback);
    return () => {
      const index = this.connectivityListeners.indexOf(callback);
      if (index > -1) {
        this.connectivityListeners.splice(index, 1);
      }
    };
  }

  // Private helper methods
  private async addToQueue(request: OfflineRequest): Promise<void> {
    if (!this.db) return;

    try {
      const existingQueue = await this.db.get('offlineQueue', 'queue') || [];
      const updatedQueue = [...existingQueue, request];
      
      await this.db.put('offlineQueue', updatedQueue, 'queue');
      this.notifyQueueListeners(updatedQueue.length);
      
      console.log('Request added to offline queue:', request.id);
    } catch (error) {
      console.error('Error adding to queue:', error);
    }
  }

  private async removeFromQueue(requestId: string): Promise<void> {
    if (!this.db) return;

    try {
      const existingQueue = await this.db.get('offlineQueue', 'queue') || [];
      const updatedQueue = existingQueue.filter(req => req.id !== requestId);
      
      await this.db.put('offlineQueue', updatedQueue, 'queue');
      this.notifyQueueListeners(updatedQueue.length);
    } catch (error) {
      console.error('Error removing from queue:', error);
    }
  }

  private async incrementRetryCount(requestId: string): Promise<void> {
    if (!this.db) return;

    try {
      const existingQueue = await this.db.get('offlineQueue', 'queue') || [];
      const updatedQueue = existingQueue.map(req => {
        if (req.id === requestId) {
          req.retryCount++;
          
          // Remove after 3 retries
          if (req.retryCount >= 3) {
            console.log('Request exceeded retry limit, removing:', requestId);
            return null;
          }
        }
        return req;
      }).filter(req => req !== null) as OfflineRequest[];
      
      await this.db.put('offlineQueue', updatedQueue, 'queue');
      this.notifyQueueListeners(updatedQueue.length);
    } catch (error) {
      console.error('Error incrementing retry count:', error);
    }
  }

  private notifyQueueListeners(queueLength: number): void {
    this.queueListeners.forEach(callback => callback(queueLength));
  }

  private notifyConnectivityListeners(isOnline: boolean): void {
    this.connectivityListeners.forEach(callback => callback(isOnline));
  }

  private generateRequestId(): string {
    return 'req_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
  }
}

// Create singleton instance
export const offlineManager = new OfflineManager();

// Export types
export type { OfflineRequest, CachedResponse };
